import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Modal, Button, tokens } from '../design-system';

// Feature descriptions for the upgrade modal
const FEATURE_INFO = {
  github_integration: {
    title: 'GitHub Integration',
    description: 'Connect your GitHub account and push generated code directly to repositories',
    icon: '🐙',
    minTier: 'Pro',
  },
  export: {
    title: 'Export',
    description: 'Export your plans as JSON, Markdown, YAML, or CSV for use with other tools',
    icon: '⬇️',
    minTier: 'Hobbyist',
  },
  chat: {
    title: 'Plan Assistant',
    description: 'Chat with an AI assistant that has full context about your plan to answer questions, explain architecture, and help with planning',
    icon: '💬',
    minTier: 'Hobbyist',
  },
};

const TIER_DETAILS = {
  Hobbyist: {
    price: '$10',
    benefits: ['Export to JSON/Markdown/YAML', 'Plan Assistant (AI Chat)', 'AGENTS.md Bundle'],
  },
  Pro: {
    price: '$50',
    benefits: ['GitHub Integration & Drift Detection', 'Jira & Trello Sync', 'Everything in Hobbyist'],
  },
};

const UpgradeModal = ({ isOpen, onClose, feature }) => {
  const navigate = useNavigate();
  const posthog = usePostHog();

  const featureInfo = FEATURE_INFO[feature] || {
    title: 'Pro Feature',
    description: 'This feature is available on the Pro plan',
    icon: '⭐',
    minTier: 'Pro',
  };

  const tierName = featureInfo.minTier || 'Pro';
  const tierDetails = TIER_DETAILS[tierName] || TIER_DETAILS.Pro;

  React.useEffect(() => {
    if (isOpen) {
      posthog?.capture('upgrade_modal_shown', { feature, tier: tierName });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpgrade = () => {
    posthog?.capture('upgrade_clicked', { feature, tier: tierName });
    onClose();
    navigate('/pricing');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: tokens.spacing[6] }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          margin: '0 auto',
          marginBottom: tokens.spacing[4],
        }}>
          {featureInfo.icon}
        </div>
        <h2 style={{
          margin: 0,
          marginBottom: tokens.spacing[2],
          fontSize: tokens.typography.fontSize['2xl'],
          fontWeight: tokens.typography.fontWeight.bold,
          color: 'var(--color-text)',
        }}>
          Upgrade to {tierName}
        </h2>
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: tokens.typography.fontSize.sm }}>
          {featureInfo.title} requires a {tierName} subscription
        </p>
      </div>

      {/* Feature Description */}
      <div style={{
        padding: tokens.spacing[4],
        borderRadius: tokens.borderRadius.md,
        backgroundColor: 'var(--color-bg)',
        marginBottom: tokens.spacing[5],
      }}>
        <p style={{ margin: 0, color: 'var(--color-text)', fontSize: tokens.typography.fontSize.sm }}>
          {featureInfo.description}
        </p>
      </div>

      {/* Benefits */}
      <div style={{ marginBottom: tokens.spacing[5] }}>
        <p style={{
          margin: 0,
          marginBottom: tokens.spacing[3],
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.semibold,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          What you'll get with {tierName}
        </p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
          {tierDetails.benefits.map((benefit, index) => (
            <li key={index} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill={tokens.colors.success[500]} style={{ flexShrink: 0 }}>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span style={{ color: 'var(--color-text)', fontSize: tokens.typography.fontSize.sm }}>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Price */}
      <div style={{ textAlign: 'center', marginBottom: tokens.spacing[6] }}>
        <span style={{
          fontSize: tokens.typography.fontSize['3xl'],
          fontWeight: tokens.typography.fontWeight.bold,
          color: 'var(--color-text)',
        }}>
          {tierDetails.price}
        </span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: tokens.typography.fontSize.sm }}>/month</span>
      </div>

      {/* Trial note for Pro */}
      {tierName === 'Pro' && (
        <p style={{
          textAlign: 'center',
          fontSize: tokens.typography.fontSize.xs[0],
          color: 'var(--color-text-muted)',
          marginBottom: tokens.spacing[4],
          marginTop: 0,
        }}>
          7-day free trial — no charge until your trial ends
        </p>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: tokens.spacing[3] }}>
        <Button variant="secondary" onClick={onClose}>
          Maybe Later
        </Button>
        <Button variant="primary" onClick={handleUpgrade}>
          {tierName === 'Pro' ? 'Start Free Trial' : 'Upgrade Now'}
        </Button>
      </div>
    </Modal>
  );
};

// Simple prompt component for inline use
export const UpgradePrompt = ({ feature, className = '' }) => {
  const navigate = useNavigate();
  const featureInfo = FEATURE_INFO[feature] || {
    title: 'Pro Feature',
    description: 'Available with Pro',
    icon: '⭐',
    minTier: 'Pro',
  };

  const tierName = featureInfo.minTier || 'Pro';

  return (
    <div
      className={className}
      style={{
        padding: tokens.spacing[4],
        borderRadius: tokens.borderRadius.lg,
        textAlign: 'center',
        backgroundColor: 'rgba(249, 115, 22, 0.05)',
        border: '1px solid rgba(249, 115, 22, 0.2)',
      }}
    >
      <div style={{ marginBottom: tokens.spacing[2], fontSize: 24 }}>
        {featureInfo.icon}
      </div>
      <p style={{ margin: 0, marginBottom: tokens.spacing[1], fontWeight: tokens.typography.fontWeight.medium, color: 'var(--color-text)' }}>
        {featureInfo.title}
      </p>
      <p style={{ margin: 0, marginBottom: tokens.spacing[3], fontSize: tokens.typography.fontSize.sm, color: 'var(--color-text-muted)' }}>
        {featureInfo.description}
      </p>
      <Button variant="primary" size="sm" onClick={() => navigate('/pricing')}>
        Upgrade to {tierName}
      </Button>
    </div>
  );
};

export default UpgradeModal;
